<template>
  <CCard>
    <CCardHeader><h2>ユーザ更新</h2></CCardHeader>
    <CCardBody>
      <p class="preika-required-note">
        <span class="preika-required-note__mark" aria-hidden="true">*</span>
        <span>が付いている項目は必須入力項目です</span>
      </p>

      <CAlert color="info">
        会社管理者は社員情報と個人連絡先を確認・代理更新できます。電話番号、住所、SlackメンバーIDは、本人も「個人設定」から変更できます。パスワードは本人が個人設定から変更します。
      </CAlert>
      <CAlert v-if="loadError" color="danger">{{ loadError }}</CAlert>
      <div v-if="initialLoading" class="text-center py-5">
        <CSpinner color="primary"/> 読み込んでいます
      </div>

      <ValidationObserver v-else ref="obs" v-slot="ObserverProps">
        <CRow v-if="message.length">
          <CCol sm="12">
            <ul class="text-danger">
              <li v-for="error in message" :key="error">{{ error }}</li>
            </ul>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="社員番号" rules="employee|max:20" v-slot="ProviderProps">
              <CInput label="社員番号" v-model="items.employee_id"/>
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="氏名" rules="required|name:20" v-slot="ProviderProps">
              <label>氏名<span class="asterisk">*</span></label>
              <CInput v-model="items.name" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="電話番号" rules="tel" v-slot="ProviderProps">
              <CInput label="電話番号" v-model="items.telephone" description="ハイフンなしで入力してください" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="住所" rules="address|max:60" v-slot="ProviderProps">
              <CInput label="住所" v-model="items.address" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="E-Mail" rules="required|email|max:254" v-slot="ProviderProps">
              <label>ログインE-Mail<span class="asterisk">*</span></label>
              <CInput v-model="items.email" @change="re_mail = ''" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="E-Mail(確認)" rules="required|email|max:254|check:E-Mail" v-slot="ProviderProps">
              <label>ログインE-Mail(確認)<span class="asterisk">*</span></label>
              <CInput v-model="re_mail" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <label>入社年月日</label>
            <CInput type="date" v-model="items.hire_date" />
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <label>所属部署</label>
            <select class="custom-select" v-model="items.department_id">
              <option :value="0">未選択</option>
              <option v-for="option in departmentOptions" :value="option.id" :key="option.id">
                {{ option.display_name || option.name }}
              </option>
            </select>
          </CCol>
        </CRow>

        <CRow class="mt-3">
          <CCol sm="12" md="10" lg="8">
            <fieldset class="role-fieldset">
              <legend>役割<span class="asterisk">*</span></legend>
              <p class="text-muted small mb-3">
                このユーザーが利用できる機能を役割で設定します。複数選択できます。
              </p>
              <div class="role-grid">
                <label
                  v-for="role in roles"
                  :key="role.id"
                  class="role-option"
                  :class="{ 'role-option--selected': isRoleSelected(role.id) }"
                >
                  <input type="checkbox" :checked="isRoleSelected(role.id)" @change="toggleRole(role.id, $event.target.checked)">
                  <span>
                    <strong>{{ role.name }}</strong>
                    <small v-if="role.description">{{ role.description }}</small>
                  </span>
                </label>
              </div>
              <p v-if="!selectedRoleIds.length" class="text-danger small mt-2">
                少なくとも1つの役割を選択してください。
              </p>
            </fieldset>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <div class="d-flex align-items-center">
              <label class="custom-switch mb-0">
                <input type="checkbox" class="custom-switch-input" id="approver-flg-edit" v-model="items.approver_flg" />
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <label for="approver-flg-edit" class="ml-2 mb-0">申請承認者</label>
            </div>
          </CCol>
          <CCol sm="12" md="6" lg="4">
            <div class="d-flex align-items-center">
              <label class="custom-switch mb-0">
                <input type="checkbox" class="custom-switch-input" id="attendance-exempt-flg-edit" v-model="items.attendance_exempt_flg" />
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <label for="attendance-exempt-flg-edit" class="ml-2 mb-0">勤怠管理の対象外</label>
            </div>
            <small class="form-text text-muted">
              取締役など、勤務表を出さない方に設定します。月次集計・月次締め・
              全体の勤怠状況に並ばなくなります。ログイン・承認・本人の勤務表入力は
              今までどおりです。
            </small>
          </CCol>
        </CRow>

        <!--
          週稼働日数・稼働曜日・週の所定労働時間。登録画面と同じ部品を使う。
          以前は「4日/3日/2日/1日」が未実装で押せなかった。
        -->
        <WorkPatternInput
          :working-days="Number(items.working_days)"
          :work-weekdays="items.work_weekdays || ''"
          :daily-work-minutes="workWeeklyMinutes"
          @update:workingDays="items.working_days = $event"
          @update:workWeekdays="items.work_weekdays = $event"
          @update:dailyWorkMinutes="items.daily_work_minutes = $event"
          @validity="work_pattern_valid = $event"
        />

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="有給日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="有給日数" v-model="items.paid_days" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="代休日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="代休日数" v-model="items.comp_days" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="今期有休消化日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="今期有休消化日数" v-model="items.paid_days_used" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <!--
              有給付与月は「月」なので1〜12から選ぶ。

              以前は有休日数と同じ数値の入力欄で、空のまま送ると
              Number('') が 0 になり、0 で保存されていた。付与SQLは
              1〜12 としか一致しないので、**その人には一度も付与されない**。
              画面にもログにも出ないので、気づくのは本人が有休を取ろうと
              したときになる。
            -->
            <label>有給付与月<span class="asterisk">*</span></label>
            <select class="custom-select" v-model="items.paid_days_add">
              <option value="">未選択</option>
              <option v-for="month in grantMonthOptions" :value="month" :key="'grant-' + month">
                {{ month }}月
              </option>
            </select>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="固定内残業" rules="total_time" v-slot="ProviderProps">
              <CInput label="固定内残業" v-model="items.fixed_over_time" placeholder="00:00" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="SlackメンバーID" rules="max:20" v-slot="ProviderProps">
              <CInput label="SlackメンバーID" v-model="items.slack_id" description="例：U0123456789" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow class="mt-3 align-items-center">
          <CCol class="text-start">
            <button type="button" class="btn btn-secondary" @click="valueCheck">戻る</button>
          </CCol>
          <CCol class="text-center">
            <!--
              **押せない理由を、押せないボタンの隣に出す。**
            
              有給付与月は必須なのに印が無く、しかも 0 で作られた人は
              画面を開いた瞬間から押せなかった。**ほかの人は普通に更新
              できるので、「この人だけ壊れている」としか見えない。**
            -->
            <CAlert v-if="missingFields.length" color="warning" class="mb-3">
              <b>あと{{ missingFields.length }}つ選ぶと、確認へ進めます。</b>
              <ul class="mb-0 mt-2">
                <li v-for="what in missingFields" :key="what">{{ what }}</li>
              </ul>
            </CAlert>

            <button
              type="button"
              @click="openModal"
              class="btn btn-info"
              :disabled="ObserverProps.invalid || loading || !selectedRoleIds.length || !work_pattern_valid || !items.paid_days_add"
            >
              確認
            </button>
            <UpdateModal
              :employee="items.employee_id"
              :user="items.name"
              :tel="items.telephone"
              :live="items.address"
              :mail="items.email"
              :hire_date="items.hire_date"
              :department_name="selectedDepartmentLabel()"
              :role_names="selectedRoleNames"
              :approver_flg="items.approver_flg"
              :attendance_exempt_flg="items.attendance_exempt_flg"
              :working_days="items.working_days"
              :paid="Number(items.paid_days)"
              :comp="Number(items.comp_days)"
              :paid_used="Number(items.paid_days_used)"
              :paid_add="Number(items.paid_days_add)"
              :fixed_over_time="items.fixed_over_time"
              :slack_id="items.slack_id"
              :loading="loading"
              :isHolidayFields="isHolidayFields"
              v-show="showModal"
              @update="update"
              @close="closeModal"
            />
          </CCol>
          <CCol></CCol>
        </CRow>
      </ValidationObserver>
    </CCardBody>
  </CCard>
</template>

<script>
import ConstList from './../ConstList'
import UpdateModal from './UpdateModal'
import WorkPatternInput from './WorkPatternInput'
import axios from 'axios'
import { defaultRoleIds, normalizeRoles, roleNames, toggleRoleId } from './roleSupport'

const emptyUser = () => ({
  employee_id: '',
  name: '',
  telephone: '',
  address: '',
  email: '',
  hire_date: '',
  department_id: 0,
  department_name: '',
  approver_flg: false,
  attendance_exempt_flg: false,
  working_days: 5,
  work_weekdays: '',
  daily_work_minutes: null,
  paid_days: 0,
  comp_days: 0,
  paid_days_used: 0,
  // 未選択は空。0 だと「0月」として保存され、一度も付与されない。
  paid_days_add: '',
  fixed_over_time: '00:00',
  slack_id: '',
  role_ids: [],
  role_names: []
})

export default {
  name: 'UpdateInput',
  components: { UpdateModal, WorkPatternInput },
  data () {
    return {
      showModal: false,
      items: emptyUser(),
      re_mail: '',
      departmentOptions: [],
      roles: [],
      selectedRoleIds: [],
      message: [],
      id: null,
      loading: false,
      initialLoading: true,
      loadError: '',
      isHolidayFields: true,
      work_pattern_valid: true
    }
  },
  computed: {
    /**
     * まだ選ばれていない必須項目の名前。
     *
     * **押せない条件と、ここは同じものを見る。**片方だけ直すと、
     * 「あと0つです」と出ているのに押せない、という形になる。
     *
     * 画面に出ている名前をそのまま使う。中の変数名を出しても、
     * どこを直せばよいか分からない。
     */
    missingFields () {
      const missing = []
      if (!this.items.paid_days_add) { missing.push('有給付与月') }
      if (!this.selectedRoleIds.length) { missing.push('役割（1つ以上）') }
      if (!this.work_pattern_valid) { missing.push('勤務形態（曜日と1日の時間）') }
      return missing
    },
    /** 有給付与月は1〜12から選ぶ。 */
    grantMonthOptions () {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    },
    selectedRoleNames () {
      return roleNames(this.roles, this.selectedRoleIds)
    },
    /*
     * サーバは数値（分）で返し、部品は文字列で扱う。
     *
     * 文字列にするのは、**未設定（null）と「未設定へ戻す」（空文字）を
     * 区別する**ため。空文字を送ると NULL になり、項目ごと送らなければ
     * 現在値が維持される。数値だと 0 と未設定が混ざる。
     */
    workWeeklyMinutes () {
      const minutes = this.items.daily_work_minutes
      return minutes === null || minutes === undefined || minutes === ''
        ? ''
        : String(minutes)
    }
  },
  mounted () {
    this.loadPage()
  },
  methods: {
    async loadPage () {
      this.initialLoading = true
      this.loadError = ''
      const parsedId = Number(this.$route.params.id)
      if (!Number.isInteger(parsedId) || parsedId <= 0) {
        this.loadError = 'ユーザーを特定できませんでした。ユーザー一覧から開き直してください。'
        this.initialLoading = false
        return
      }
      this.id = parsedId
      try {
        const [userResponse, roleResponse] = await Promise.all([
          axios.get(process.env.VUE_APP_API_ORIGIN + '/users/' + this.id),
          axios.get(process.env.VUE_APP_API_ORIGIN + '/users/roles')
        ])
        this.items = Object.assign(emptyUser(), userResponse.data || {})

        /*
         * **0 は「未選択」として扱う。**
         *
         * 選べるのは 1〜12 だけなので、0 はどの選択肢にも一致せず、
         * 見た目は「未選択」になる。ところが確認ボタンの条件は
         * `!items.paid_days_add` なので、**画面を開いた瞬間から
         * 押せない。**電話番号を1文字直すこともできなくなる。
         *
         * 0 のまま作られた人が実在する（付与の点検SQLが
         * paid_days_add < 1 を探している）。ここで空へ寄せて、
         * 印と名指しで選び直してもらう。
         *
         * **勝手に既定値を入れない。**入れると、選んだつもりのない
         * 月で有休が付く。どの月にするかは決められる人が決める。
         */
        const grantMonth = Number(this.items.paid_days_add)
        if (!Number.isFinite(grantMonth)
            || grantMonth < 1 || grantMonth > 12) {
          this.items.paid_days_add = ''
        }
        this.re_mail = this.items.email || ''
        this.roles = normalizeRoles(roleResponse.data)
        const currentIds = Array.isArray(this.items.role_ids) ? this.items.role_ids : []
        this.selectedRoleIds = currentIds.length ? currentIds.slice() : defaultRoleIds(this.roles)
        this.isHolidayFields = this.items.isHolidayFields === true
        await this.fetchDepartmentOptions()
      } catch (error) {
        this.loadError = this.errorMessage(error, 'ユーザー情報を取得できませんでした。')
      } finally {
        this.initialLoading = false
      }
    },
    valueCheck () {
      if (confirm(ConstList.TRANSITION)) {
        this.$router.push({ name: ConstList.ROUTE_INFO, params: { id: this.id } })
      }
    },
    isRoleSelected (roleId) {
      return this.selectedRoleIds.some(value => String(value) === String(roleId))
    },
    toggleRole (roleId, checked) {
      this.selectedRoleIds = toggleRoleId(this.selectedRoleIds, roleId, checked)
    },
    openModal () {
      this.message = []
      if (!this.selectedRoleIds.length) {
        this.message.push('少なくとも1つの役割を選択してください。')
        return
      }
      this.showModal = true
      this.isHolidayFields = this.items.isHolidayFields === true
    },
    update () {
      this.loading = true
      this.message = []
      const params = new URLSearchParams()
      params.append(ConstList.EMPLOYEE_ID, this.items.employee_id)
      params.append(ConstList.NAME, this.items.name)
      params.append(ConstList.TELEPHONE, this.items.telephone || '')
      params.append(ConstList.ADDRESS, this.items.address || '')
      params.append(ConstList.EMAIL, this.items.email)
      params.append(ConstList.HIRE_DATE, this.items.hire_date || '')
      const departmentId = this.items.department_id === '' || this.items.department_id == null
        ? 0
        : Number(this.items.department_id)
      params.append(ConstList.DEPARTMENT_ID, departmentId)
      params.append(ConstList.APPROVER_FLG, this.items.approver_flg)
      params.append(ConstList.ATTENDANCE_EXEMPT_FLG, this.items.attendance_exempt_flg)
      params.append(ConstList.PAID_DAYS, Number(this.items.paid_days))
      params.append(ConstList.COMP_DAYS, Number(this.items.comp_days))
      params.append(ConstList.PAID_DAYS_USED, Number(this.items.paid_days_used))
      params.append(ConstList.PAID_DAYS_ADD, Number(this.items.paid_days_add))
      params.append(ConstList.WORKING_DAYS, Number(this.items.working_days))
      // 空文字は「未設定へ戻す」。項目ごと送らないのとは意味が違う。
      params.append(ConstList.WORK_WEEKDAYS, this.items.work_weekdays || '')
      params.append(ConstList.DAILY_WORK_MINUTES, this.workWeeklyMinutes)
      params.append(ConstList.FIXED_OVER_TIME, this.items.fixed_over_time)
      params.append(ConstList.SLACK_ID, this.items.slack_id || '')
      this.selectedRoleIds.forEach(roleId => params.append('roleIds', String(roleId)))

      axios.post(process.env.VUE_APP_API_ORIGIN + '/users/' + this.id, params)
        .then(response => {
          if (response.data !== ConstList.SUCCESS) {
            throw new Error('ユーザーを更新できませんでした。役割設定を確認してください。')
          }
          alert(ConstList.UPDATE_MESSAGE)
          this.$router.push({ name: ConstList.ROUTE_LIST })
        })
        .catch(error => {
          this.showModal = false
          this.message = [this.errorMessage(error, 'ユーザーを更新できませんでした。')]
        })
        .finally(() => { this.loading = false })
    },
    closeModal () {
      this.showModal = false
    },
    selectedDepartmentLabel () {
      if (this.items.department_id == null || this.items.department_id === '' || Number(this.items.department_id) === 0) return ''
      const dept = this.departmentOptions.find(({ id }) => String(id) === String(this.items.department_id))
      return dept ? (dept.display_name || dept.name) : (this.items.department_name || '（現在無効）')
    },
    mergeCurrentDepartmentOption () {
      if (this.items.department_id == null || this.items.department_id === '' || Number(this.items.department_id) === 0) return
      if (this.departmentOptions.some(({ id }) => String(id) === String(this.items.department_id))) return
      let label = this.items.department_name || '（現在無効）'
      if (label.indexOf('（現在無効）') === -1) label += '（現在無効）'
      this.departmentOptions.push({
        id: this.items.department_id,
        name: this.items.department_name || '',
        display_name: label
      })
    },
    fetchDepartmentOptions () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/departments/count')
        .then(countResponse => {
          const totalCount = Number(countResponse.data)
          const allCount = Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 1
          return axios.get(process.env.VUE_APP_API_ORIGIN + '/departments', {
            params: { currentPage: 1, pageSize: allCount }
          })
        })
        .then(response => {
          const list = Array.isArray(response.data) ? response.data : []
          this.departmentOptions = list
            .filter(item => Number(item.deleted_flg) !== ConstList.DELETE)
            .map(item => ({ id: item.id, name: item.name }))
          this.mergeCurrentDepartmentOption()
        })
    },
    errorMessage (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data && data !== 'NG') return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
button:disabled { cursor: not-allowed; }
.role-fieldset { border: 1px solid #cbd5e1; border-radius: 12px; padding: 16px; }
.role-fieldset legend { width: auto; padding: 0 8px; margin: 0; font-size: 14px; font-weight: 700; }
.role-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; }
.role-option { display: flex; gap: 10px; align-items: flex-start; margin: 0; padding: 12px; border: 1px solid #dbe5ec; border-radius: 10px; cursor: pointer; }
.role-option--selected { border-color: #2584c5; background: #eff8ff; }
.role-option input { margin-top: 3px; }
.role-option span { display: grid; gap: 3px; }
.role-option small { color: #64748b; line-height: 1.45; }
</style>
